import { useState, useEffect, useCallback, useRef } from 'react';
import { MenuItem } from '../types';

const findActiveParents = (items: MenuItem[], targetPath: string, parentKey = ''): string[] => {
    for (const item of items) {
        const itemKey = parentKey ? `${parentKey}.${item.id || item.label}` : (item.id || item.label);
        if (item.path === targetPath || item.activePaths?.includes(targetPath)) return parentKey ? [parentKey] : [];
        if (item.subMenu?.length) {
            const found = findActiveParents(item.subMenu, targetPath, itemKey);
            if (found.length) return parentKey ? [parentKey, ...found] : found;
        }
    }
    return [];
};

export const useSidebarMenu = (menuItems: MenuItem[], currentPath: string, isMenuOpen?: boolean) => {
    const [expandedItems, setExpandedItems] = useState<string[]>([]);
    const [savedExpandedState, setSavedExpandedState] = useState<string[]>([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const previousMenuOpen = useRef<boolean | undefined>(isMenuOpen);

    useEffect(() => {
        if (previousMenuOpen.current !== isMenuOpen) {
            if (isMenuOpen === false) {
                setSavedExpandedState(expandedItems);
                setExpandedItems([]);
            } else if (isMenuOpen === true && previousMenuOpen.current === false) {
                setExpandedItems([]);
            }
            previousMenuOpen.current = isMenuOpen;
        }
    }, [isMenuOpen, expandedItems]);

    useEffect(() => {
        if (isInitialLoad && menuItems.length && currentPath) {
            const activeParents = findActiveParents(menuItems, currentPath);
            setExpandedItems(activeParents);
            setSavedExpandedState(activeParents);
            setIsInitialLoad(false);
        }
    }, [menuItems, currentPath, isInitialLoad]);

    useEffect(() => {
        if (isInitialLoad || isMenuOpen === false || !menuItems.length || !currentPath) return;

        const activeParents = findActiveParents(menuItems, currentPath);
        if (activeParents.length === 0) return;

        setExpandedItems(prev => {
            const next = Array.from(new Set([...prev, ...activeParents]));
            return next.length === prev.length ? prev : next;
        });
        setSavedExpandedState(prev => Array.from(new Set([...prev, ...activeParents])));
    }, [menuItems, currentPath, isInitialLoad, isMenuOpen]);

    const handleToggleExpand = useCallback((itemKey: string, level = 0) => {
        setExpandedItems(prev => {
            const isOpen = prev.includes(itemKey);
            let newExpandedItems: string[];
            
            if (level === 0) {
                newExpandedItems = isOpen ? [] : [itemKey];
            } else {
                if (isOpen) {
                    newExpandedItems = prev.filter(key => key !== itemKey && !key.startsWith(`${itemKey}.`));
                } else {
                    const rootKey = itemKey.split('.')[0];
                    newExpandedItems = [...prev.filter(key => !key.startsWith(`${rootKey}.`) || key.startsWith(`${itemKey}.`)), itemKey];
                }
            }
            
            if (isMenuOpen !== false) {
                setSavedExpandedState(newExpandedItems);
            }
            
            return newExpandedItems;
        });
    }, [isMenuOpen]);

    return { expandedItems, handleToggleExpand };
};
